import React, { useState, Suspense, FC, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import PreFooter from '../../components/prefooter';
import Challenge from '../../components/usecases/challenge';
import Main from '../../components/usecases/main';
import OtherCases from '../../components/usecases/others';
import Results from '../../components/usecases/results';
import LogoBlue from './../../images/common/logo.png';
import HeroIcon1 from './../../images/solutions/usecase-card-1-icon.svg';
import HeroIcon2 from './../../images/solutions/usecase-card-2-icon.svg';
import HeroIcon3 from './../../images/solutions/usecase-card-3-icon.svg';
import HeroIcon4 from './../../images/solutions/usecase-card-4-icon.svg';
import Hero1 from './../../images/usecases/hero1.png';
import Hero2 from './../../images/usecases/hero2.png';
import Hero3 from './../../images/usecases/hero3.png';
import Hero4 from './../../images/usecases/hero4.png';
import ResultImage1 from './../../images/usecases/results-image1.png';
import ResultImage2 from './../../images/usecases/results-image2.png';
import ResultImage3 from './../../images/usecases/results-image3.png';
import ResultImage4 from './../../images/usecases/results-image4.png';

interface Props {}

const UseCases: FC<Props> = () => {
  const { t } = useTranslation();
  const cardPath = [...Array(4)].map((_, index) => {
    return `${index + 1}-${t(`SolutionUseCasesCard${index + 1}Title`)
      .toLowerCase()
      .replace(/ /g, '-')}`.toString();
  });

  const [useCase, setUseCase] = useState<string | undefined>(cardPath[0]);
  const [useCaseNext, setUseCaseNext] = useState<string | undefined>(
    cardPath[1]
  );
  const [currentHero, setCurrentHero] = useState<string>(Hero1);
  const [heroIcon, setHeroIcon] = useState<string>(HeroIcon1);
  const [currentResultImage, setCurrentResultImage] =
    useState<string>(ResultImage1);

  let param: { id: string | undefined } = useParams();
  let history = useHistory();

  useEffect(() => {
    setUseCase((param.id as string)[0]);
    if (param.id === cardPath[0]) {
      setCurrentHero(Hero1);
      setHeroIcon(HeroIcon1);
      setCurrentResultImage(ResultImage1);
      setUseCaseNext(cardPath[1]);
    } else if (param.id === cardPath[1]) {
      setCurrentHero(Hero2);
      setHeroIcon(HeroIcon2);
      setCurrentResultImage(ResultImage2);
      setUseCaseNext(cardPath[2]);
    } else if (param.id === cardPath[2]) {
      setCurrentHero(Hero3);
      setHeroIcon(HeroIcon3);
      setCurrentResultImage(ResultImage3);
      setUseCaseNext(cardPath[3]);
    } else if (param.id === cardPath[3]) {
      setCurrentHero(Hero4);
      setHeroIcon(HeroIcon4);
      setCurrentResultImage(ResultImage4);
      setUseCaseNext(cardPath[0]);
    } else {
      history.push('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.id]);

  const getMetas = (useCase: string) => {
    switch (useCase[0]) {
      case '1':
        return (
          <Helmet>
            <title>Mutt Data | Accurately match supply and demand</title>
            <meta
              property="og:title"
              content="Mutt Data | Accurately match supply and demand"
            />
            <meta
              name="description"
              content="Use historical operational data and contextual information to accurately forecast demand, and efficiently assign resources from work schedules to production capacity."
            />
            <meta
              property="og:description"
              content="Use historical operational data and contextual information to accurately forecast demand, and efficiently assign resources from work schedules to production capacity."
            />
            <meta
              name="keywords"
              content="
				forecasting
				demand forecasting
				timeseries forecasting
				supply optimization
				constraint programming
				linear programming
				"
            />
            <meta
              property="og:image"
              content={`https://muttdata.ai/email/logo.png`}
            />
          </Helmet>
        );
      case '2':
        return (
          <Helmet>
            <title>
              Mutt Data | Scale your business with custom Machine Learning
            </title>
            <meta
              name="description"
              content="Using Machine Learning to power and optimize your company’s processes involves much more than just training a model and doing some predictions."
            />
            <meta
              property="og:title"
              content="Mutt Data | Scale your business with custom Machine Learning"
            />
            <meta
              property="og:description"
              content="Using Machine Learning to power and optimize your company’s processes involves much more than just training a model and doing some predictions."
            />
            <meta
              name="keywords"
              content="
				machine learning
				ml-ops
				ml operations
				custom-ml
				adtech
				dsp
				RTB
				CTR prediction
				"
            />
            <meta
              property="og:image"
              content={`https://muttdata.ai/email/logo.png`}
            />
          </Helmet>
        );
      case '3':
        return (
          <Helmet>
            <title>Mutt Data | Unlock online marketing optimization</title>
            <meta
              name="description"
              content="Connect millions of different data points across multiple marketing campaigns in one dashboard to effortlessly optimize for ROAS, retention rates and overall customer LTV."
            />
            <meta
              property="og:title"
              content="Mutt Data | Unlock online marketing optimization"
            />
            <meta
              property="og:description"
              content="Connect millions of different data points across multiple marketing campaigns in one dashboard to effortlessly optimize for ROAS, retention rates and overall customer LTV."
            />
            <meta
              name="keywords"
              content="
				online marketing
				paid online media
				PPC
				Social
				budget allocation
				performance forecasting
				gmv
				revenue maximization
				ROI
				ROAS
				"
            />
            <meta
              property="og:image"
              content={`https://muttdata.ai${LogoBlue}`}
            />
          </Helmet>
        );
      case '4':
        return (
          <Helmet>
            <title>
              Mutt Data | Automatically monitor KPIs with Anomaly Detection
            </title>
            <meta
              name="description"
              content="Surface relevant changes on business metrics and digested updates to make quick, informed decisions."
            />
            <meta
              property="og:title"
              content="Mutt Data | Automatically monitor KPIs with Anomaly Detection"
            />
            <meta
              property="og:description"
              content="Surface relevant changes on business metrics and digested updates to make quick, informed decisions."
            />
            <meta
              name="keywords"
              content="
				anomaly detection
				outlier detection
				root cause analysis
				revenue assurance
				"
            />
            <meta
              property="og:image"
              content={`https://muttdata.ai/email/logo.png`}
            />
          </Helmet>
        );
      default:
        return (
          <Helmet>
            <title>Mutt Data | Accurately match supply and demand</title>
            <meta
              property="og:title"
              content="Mutt Data | Accurately match supply and demand"
            />
            <meta
              name="description"
              content="Use historical operational data and contextual information to accurately forecast demand, and efficiently assign resources from work schedules to production capacity."
            />
            <meta
              property="og:description"
              content="Use historical operational data and contextual information to accurately forecast demand, and efficiently assign resources from work schedules to production capacity."
            />
            <meta
              name="keywords"
              content="
				forecasting
				demand forecasting
				timeseries forecasting
				supply optimization
				constraint programming
				linear programming
				"
            />
            <meta
              property="og:image"
              content={`https://muttdata.ai/email/logo.png`}
            />
          </Helmet>
        );
    }
  };

  return (
    <Suspense fallback={<div />}>
      {getMetas(useCase as string)}
      <Main
        currentCase={useCase}
        nextCase={useCaseNext}
        hero={currentHero}
        icon={heroIcon}
        mainTitle={t(`UseCaseMainTitle${useCase}`)}
        subTitle={t(`UseCaseMainSubtitle${useCase}`)}
        card1={t(`UseCaseMainCardText${useCase}1`)}
        card2={t(`UseCaseMainCardText${useCase}2`)}
        card3={t(`UseCaseMainCardText${useCase}3`)}
      />

      <Challenge
        headLineText={t(`UseCaseChallengeHeadline${useCase}`)}
        challengeText={t(`UseCaseChallengeChallenge${useCase}`)}
        solutionText={t(`UseCaseChallengeSolution${useCase}`)}
      />

      <Results
        text={t(`UseCaseResultsText${useCase}`)}
        resultImage={currentResultImage}
      />

      <OtherCases currentCase={useCase} />

      <PreFooter
        title={t('UseCasePrefooterTitle')}
        text={t('UseCasePrefooterText')}
        btnText={t('UseCasePrefooterButton')}
        hasContentOnTop={false}
      />
    </Suspense>
  );
};

export default UseCases;
